// Bulk-hook: haalt de signaal-data voor N entities op via de Cloud Function
// getRetentionSignals en berekent per entity de scores met pure helpers.
// Eénmalige aanroep (geen listener) — refresh = component remount.
//
// Waarom via een functie en niet rechtstreeks uit Firestore: een koepel leest
// hier de EventResults, antwoorden, taken en teamleden van zijn PROJECTEN, en
// dat laten de Firestore-regels niet toe zodra de lezer van één project geen
// lid is. De functie geeft alleen tijdstippen terug (geen inhoud, geen
// deelnemers); de vensters (16 weken, 12 maanden, 30 dagen vooruit) zitten in
// de query daar.
//
// AI-CONTEXT: See docs/patterns.md#retention-derivation
// AI-CONTEXT: See functions/portfolio/getRetentionSignals.js
// AI-CONTEXT: See docs/pitfalls.md#security-open-read-rule-carries-cross-tenant-features

import { useEffect, useMemo, useState } from 'react';
import firebase from '../../firebase/compat';
import { client } from '../Client';
import buildSignals from '../../helpers/retention/buildSignals';
import computeProductScope from '../../helpers/retention/computeProductScope';
import computeActivationStatus from '../../helpers/retention/computeActivationStatus';
import computeEngagementScore from '../../helpers/retention/computeEngagementScore';
import computeWaardeRealisatieScore from '../../helpers/retention/computeWaardeRealisatieScore';

// Zet `window.__retentionDebug = true` in de browser-console om verbose logs te zien.
const debugLog = (...args) => {
    if (typeof window !== 'undefined' && window.__retentionDebug) {
        // eslint-disable-next-line no-console
        console.log('[retention]', ...args);
    }
};

// Alles wat de functie leest. Mislukt de aanroep zelf, dan is van al deze
// collecties niets bekend en zegt het scherm dat ook.
// AI-CONTEXT: See docs/inbox.md Bug #133
const ALL_COLLECTIONS = ['EventResults', 'QuestionnairesResponses', 'Tasks', 'Users'];

/**
 * @typedef {object} EntitySignalData
 * @property {Array<number>} events - ms
 * @property {Array<{t: number, f: string}>} researches - ms + FormID
 * @property {Array<number>} tasks - ms
 * @property {Array<number>} userLogins - ms
 */

// buildSignals verwacht documentvormen met een datum; de functie stuurt kale ms.
const toSignalInput = (/** @type {EntitySignalData | undefined} */ data) => ({
    entityEvents: (data?.events || []).map((t) => ({ Timestamp: new Date(t) })),
    entityResearches: (data?.researches || []).map((r) => ({ Timestamp: new Date(r.t), FormID: r.f })),
    entityTasks: (data?.tasks || []).map((t) => ({ Timestamp: new Date(t) })),
    entityUsers: (data?.userLogins || []).map((t) => ({ LastLogin: new Date(t) })),
});

const useRetentionScoresBulk = (entities) => {
    const entityIds = useMemo(
        () => (Array.isArray(entities) ? entities.map((e) => e.CompagnyID).filter(Boolean) : []),
        [entities]
    );
    const idsKey = useMemo(() => [...entityIds].sort().join(','), [entityIds]);

    const [scoresByEntityId, setScoresByEntityId] = useState(() => new Map());
    const [unavailableCollections, setUnavailableCollections] = useState(/** @type {Array<string>} */ ([]));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Alleen zetten als de inhoud echt verandert: een verse lege array is nooit
    // referentieel gelijk aan de vorige, en dwingt dus een hertekening af van
    // elke kaart die meekijkt.
    const updateUnavailable = (/** @type {Array<string>} */ names) => {
        setUnavailableCollections((previous) => {
            const next = [...names].sort();
            const same = previous.length === next.length
                && previous.every((name, index) => name === next[index]);
            return same ? previous : next;
        });
    };

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (entityIds.length === 0) {
                debugLog('no entity IDs yet, skipping fetch');
                setScoresByEntityId(new Map());
                setUnavailableCollections((previous) => (previous.length === 0 ? previous : []));
                setLoading(false);
                return;
            }

            debugLog('fetching signals for', entityIds.length, 'entities');
            setLoading(true);
            setError(null);

            try {
                const getRetentionSignals = firebase.app().functions('europe-west1').httpsCallable('getRetentionSignals');
                const response = await getRetentionSignals({ client, entityIds });
                /** @type {Record<string, EntitySignalData>} */
                const byCompagnyId = response?.data?.byCompagnyId || {};
                // Welke collecties de functie niet kon lezen. Een lege lijst
                // signalen is stil, dus dit is de enige manier om 'mislukt' van
                // 'is er niet' te scheiden.
                const failures = new Set(Array.isArray(response?.data?.unavailable) ? response.data.unavailable : []);

                debugLog('fetched signals for', Object.keys(byCompagnyId).length, 'entities');

                if (cancelled) return;

                const now = new Date();
                const result = new Map();
                let errorCount = 0;

                for (const entity of entities) {
                    const id = entity.CompagnyID;
                    if (!id) continue;
                    try {
                        const signals = buildSignals({
                            entity,
                            ...toSignalInput(byCompagnyId[id]),
                            now,
                        });
                        const productScope = computeProductScope(entity);
                        const activationStatus = computeActivationStatus(entity, signals);
                        const engagementScore = computeEngagementScore(entity, signals);
                        const waardeRealisatieScore = computeWaardeRealisatieScore(entity, signals, productScope);

                        result.set(id, {
                            productScope,
                            activationStatus,
                            engagementScore,
                            waardeRealisatieScore,
                            signals,
                        });
                    } catch (entityErr) {
                        errorCount += 1;
                        // eslint-disable-next-line no-console
                        console.warn(`[retention] skipped entity ${id} (${entity.CommunityName || '—'}):`, entityErr.message);
                    }
                }

                if (!cancelled) {
                    debugLog('computed scores for', result.size, 'entities');
                    setScoresByEntityId(result);
                    updateUnavailable([...failures]);
                    // Geen full pipeline-failure, maar wel zichtbaar maken hoeveel entities mislukten
                    if (errorCount > 0) {
                        setError(new Error(`${errorCount} van ${entities.length} entiteiten konden niet gescoord worden`));
                    }
                    setLoading(false);
                }
            } catch (err) {
                if (!cancelled) {
                    // eslint-disable-next-line no-console
                    console.error('[retention] pipeline failed', err);
                    setScoresByEntityId(new Map());
                    updateUnavailable(ALL_COLLECTIONS);
                    setError(err);
                    setLoading(false);
                }
            }
        };

        run();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsKey]);

    return { scoresByEntityId, loading, error, unavailableCollections };
};

export default useRetentionScoresBulk;
